<script setup lang="ts">
import { reactive, ref, watch } from "vue";
import {
  TuButton,
  TuInputLabel,
  TuInputText,
  FieldValidationRule,
} from "@tinyurldev/tu-ui-vue";
import { breakpointsBootstrapV5, useBreakpoints } from "@vueuse/core";

import useTranslation from "@Composables/useTranslation";
import { isValidEmail } from "@Helpers/functions/isValidEmail";

//
// Setup
const { __ } = useTranslation();

//
// Props
interface Props {
  name: string;
  email: string;
  loading?: boolean;
  errors?: Record<string, string>;
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  errors: () => ({}),
});

//
// Emit
interface ContactPayload {
  name: string;
  email: string;
}

interface Events {
  (e: "submit", payload: ContactPayload): void;
  (e: "validation-error"): void;
}

const emit = defineEmits<Events>();

//
// Data
const name = ref(props.name);
const email = ref(props.email);

const localErrors = reactive<{
  name: string | null;
  email: string | null;
}>({
  name: null,
  email: null,
});

const nameValidations: FieldValidationRule[] = [
  {
    validator: (value: string) => value.trim() !== "",
    errorMessage: __("organization_user_profile_settings.update_contact_form.name_required_error"),
  },
  {
    validator: (value: string) => value.length <= 255,
    errorMessage: __("organization_user_profile_settings.update_contact_form.name_too_long_error"),
  },
];

const emailValidations: FieldValidationRule[] = [
  {
    validator: (value: string) => value.trim() !== "",
    errorMessage: __("organization_user_profile_settings.update_contact_form.email_required_error"),
  },
  {
    validator: (value: string) => isValidEmail(value),
    errorMessage: __("organization_user_profile_settings.update_contact_form.email_invalid_error"),
  },
];

const breakpoints = useBreakpoints(breakpointsBootstrapV5);
const isMobileView = breakpoints.smallerOrEqual("xl");

//
// Methods
function cleanUpError(field: "name" | "email"): void {
  localErrors[field] = null;
}

function isFieldValid(
  field: "name" | "email",
  value: string,
  rules: FieldValidationRule[],
): boolean {
  return rules.every((rule) => {
    const isValid = rule.validator(value);

    if (!isValid) {
      localErrors[field] = typeof rule.errorMessage === "string"
        ? rule.errorMessage
        : rule.errorMessage();
    }

    return isValid;
  });
}

function onSubmit(): void {
  if (props.loading) {
    return;
  }

  const isNameValid = isFieldValid("name", name.value, nameValidations);
  const isEmailValid = isFieldValid("email", email.value, emailValidations);

  if (!isNameValid || !isEmailValid) {
    emit("validation-error");

    return;
  }

  emit("submit", { name: name.value, email: email.value });
}

//
// Watchers
watch(() => props.name, (givenName) => {
  name.value = givenName;
});

watch(() => props.email, (givenEmail) => {
  email.value = givenEmail;
});

watch(() => props.errors, (givenErrors) => {
  localErrors.name = givenErrors.name ?? null;
  localErrors.email = givenErrors.email ?? null;
});

//
// Exposed Test Vars
defineExpose({ localErrors });
</script>

<template>
  <form
    class="user-profile-form"
    :class="{ 'user-profile-form--mobile': isMobileView }"
    @submit.prevent="onSubmit"
  >
    <div class="user-profile-form__input">
      <TuInputLabel
        id="name"
        class="user-profile-form__label"
        :label="__('organization_user_profile_settings.update_contact_form.name_label')"
      />

      <TuInputText
        id="name"
        v-model="name"
        class="_name"
        :copy="{ ...(localErrors.name && { errorMessage: localErrors.name }) }"
        @focus="cleanUpError('name')"
        @focusout="cleanUpError('name')"
      />
    </div>

    <div class="user-profile-form__input">
      <TuInputLabel
        id="email"
        class="user-profile-form__label"
        :label="__('organization_user_profile_settings.update_contact_form.email_label')"
      />

      <TuInputText
        id="email"
        v-model="email"
        class="_email"
        :copy="{ ...(localErrors.email && { errorMessage: localErrors.email }) }"
        @focus="cleanUpError('email')"
        @focusout="cleanUpError('email')"
      />
    </div>

    <div class="user-profile-form__input">
      <div class="user-profile-form__submission">
        <TuButton
          class="_submit"
          :label="__('organization_user_profile_settings.update_contact_form.submit')"
          :is-disabled="loading"
          :is-block="isMobileView"
        />
      </div>
    </div>
  </form>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/border.scss" as border;
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as color;

.user-profile-form {
  $self: &;

  display: flex;
  flex-flow: column;
  gap: spacing.$xxxs;
  padding: spacing.$xxs;

  background-color: color.$background-secondary;
  border-radius: border.$medium-radius;
  border: border.$primary;

  &__input {
    display: grid;
    grid-template-areas: "side main";
    grid-template-columns: 1fr 3fr;
  }

  &__label {
    align-items: flex-start;
  }

  &__submission {
    grid-area: main;
  }

  &--mobile {
    #{ $self }__input {
      display: flex;
      flex-flow: column;
      gap: spacing.$xxxs;
    }
  }
}
</style>
